import {
	call,
	put,
	select,
	takeLatest,
	all,
	debounce,
} from 'redux-saga/effects';
import COURSE_TYPES from './types';
import { sagaWrapper, secureLocalStorage } from 'src/helpers';
import Service from 'src/service';
import {
	type IResponseMetadata,
	type IResponse,
	type ICourse,
	type IUser,
} from 'src/models';
import courseActions from './actions';
import notificationActions from '../Notifications/actions';
import { history, type RootState } from 'src/store';
import _ from 'lodash';

const Course = new Service('courses');
const Report = new Service('reports');
const User = new Service('users');

function* getAll({ payload }: any) {
	const response: IResponseMetadata = yield call(Course.getAll, payload);
	yield put(courseActions.setAll(response.data, response.metadata));
}

function* getOne({ payload }: any) {
	const { id, userId } = payload;
	let response: IResponse;
	if (userId) {
		response = yield call(Course.getPath, `${id}?userId=${userId}`);
		if (_.isEmpty(response.data)) {
			yield history.push('/app/404');
		}
	} else response = yield call(Course.getOne, id);

	yield put(courseActions.setOne(response.data));
}

function* create({ payload }: any) {
	const response: IResponse = yield call(Course.create, payload.course);
	yield history.push(`/app/courses/edit/${response.data.id}`);
	yield put(notificationActions.add('success', 'Curso creado'));
}

function* update({ payload }: any) {
	const course = payload.course;
	yield call(Course.update, course);
	yield put(courseActions.getOne(course.id));
	yield put(
		notificationActions.add('success', 'Datos del curso actualizados'),
	);
}

function* getExam({ payload }: any) {
	const { id, sectionId } = payload;
	const response: IResponse = yield call(
		Course.getPath,
		`exam/${id}${sectionId ? '?sectionId=' + sectionId : ''}`,
	);
	yield put(courseActions.setExam(response.data));
}

function* getInstitutionCourses({ payload }: any) {
	const response: IResponse = yield call(
		Course.getPath,
		`institution/${payload.institutionId}`,
	);
	yield put(courseActions.setInstitutionCourses(response.data));
}

function* getUsersCourses({ payload }: any) {
	const { institutionId, courseId } = payload;
	const response: IResponse = yield call(
		User.getPath,
		`courses?institutionId=${institutionId}&courseId=${courseId}`,
	);
	yield put(courseActions.set('usersCourses', response.data));
}

function* saveUsersCourses({ payload }: any) {
	yield call(User.createPath, 'courses', payload.usersCourses);
	yield put(notificationActions.add('success', 'Cursos actualizados'));
}

function* getByUser({ payload }: any) {
	const response: IResponse = yield call(
		Course.getPath,
		`user/${payload.userId}`,
	);
	yield put(courseActions.setAll(response.data));
}
function* getByInstitution({ payload }: any) {
	const response: IResponse = yield call(
		Course.getPath,
		`institution/${payload.institutionId}`,
	);
	yield put(courseActions.setAll(response.data));
}

function* courseStarted({ payload }: any) {
	yield call(
		Course.createPath,
		`started?userId=${payload.userId}&courseId=${payload.courseId}`,
		{},
	);
}

function* courseFinished({ payload }: any) {
	const { userId, courseId } = payload;
	yield call(
		Course.createPath,
		`finished?userId=${userId}&courseId=${courseId}`,
		{},
	);
	yield put(courseActions.getOne(courseId, userId));
}

function* updateInstructions({ payload }: any) {
	yield call(Course.updatePath, 'instructions', payload.course);
	yield put(notificationActions.add('success', 'Instrucciones actualizadas'));
	yield put(courseActions.getOne(payload.course.id));
}

function* getReport({ payload }: any) {
	let response: IResponse;
	if (payload.type === 'all') {
		response = yield call(Course.getPath, 'report');
		yield put(courseActions.setReport(payload.type, response.data));
	} else {
		const institutionId: number = yield select(
			(state: RootState) => state.auth.data?.institutionId,
		);
		response = yield call(
			Course.getPath,
			`report?institutionId=${institutionId}`,
		);
		yield put(courseActions.setReport(payload.type, response.data));
	}
}

function* downloadReport({ payload }: any) {
	const { type, institutionId, courseId, year } = payload;
	const course: ICourse = yield select((state: RootState) =>
		state.courses.courses.find((course: ICourse) => course.id == courseId),
	);

	if (type === 'advances') {
		// @ts-ignore
		yield call(
			// @ts-ignore
			Report.downloadReport,
			`${type}?institutionId=${institutionId}&courseId=${courseId}${year ? `&year=${year}` : ''
			}`,
			type,
			course.name,
		);
	} else {
		const response: IResponse = yield call(
			Report.getPath,
			`qualifications?institutionId=${institutionId}&courseId=${courseId}`,
		);
		yield put(courseActions.setReport(type, response.data));
	}
	yield put(courseActions.set('isDownloadingReport', false));
}

function* getExamReview({ payload }: any) {
	const { courseId, userId } = payload;
	const response: IResponse = yield call(
		Course.getPath,
		`exam/review/${courseId}?userId=${userId}`,
	);
	yield put(courseActions.setExamReview(response.data));
}

function* setAnsweredSection({ payload }: any) {
	if (payload.isSaved) {
		const user: IUser = yield select((state: RootState) => state.auth.data);
		const exam: ICourse = yield select(
			(state: RootState) => state.courses.exam,
		);
		const savedExams: any[] = yield secureLocalStorage.get('ues') || [];
		const examByUserIndex = savedExams.findIndex(
			(value: any) => value.userId == user.id,
		);

		if (examByUserIndex > -1) {
			savedExams[examByUserIndex] = { userId: user.id, exam };
		} else {
			savedExams.push({ userId: user.id, exam });
		}

		yield secureLocalStorage.set('ues', savedExams);
		yield history.push('/app/courses');
		yield put(notificationActions.add('success', 'Avance guardado'));
	}
}

function* getUserCertificate({ payload }: any) {
	const { userId, courseId } = payload;
	const response: IResponse = yield call(
		Course.getPath,
		`certificate/${userId}/${courseId}`,
	);
	yield put(courseActions.setUserCertificate(response.data));
}

function* downloadUserExam({ payload }: any) {
	const { userId, courseId } = payload;
	const examReview: ICourse = yield select(
		(state: RootState) => state.courses.examReview,
	);
	yield call(
		Course.download,
		`exam/${userId}/${courseId}/download`,
		`${examReview.user} - ${examReview.name}.pdf`,
	);
	yield put(courseActions.set('isDownloadingBulk', false));
}

function* downloadAllUsersExams({ payload }: any) {
	const { institutionId, courseId, year } = payload;
	const response: IResponse = yield call(
		Course.getPath,
		`users/${institutionId}/${courseId}/${year}`,
	);

	if (response.data?.some((value: any) => (value.attempt || 0) > 0)) {
		yield all(
			response.data.map((value: any) => {
				if ((value.attempt || 0) > 0)
					return call(
						Course.download,
						`exam/${value.userId}/${courseId}/download`,
						`${value.user} - ${value.courseName}.pdf`,
					);
			}),
		);
	} else {
		yield put(
			notificationActions.add(
				'info',
				'No hay información para descargar',
			),
		);
	}

	yield put(courseActions.set('isDownloadingBulk', false));
}

function* downloadUsersCertificates({ payload }: any) {
	const { institutionId, courseId, year, institutionName } = payload;
	const response: IResponseMetadata = yield call(
		User.getPath,
		`certificates/${institutionId}/${courseId}/${year}`,
	);

	if (response.data?.data?.some((value: any) => value.passed)) {
		yield all(
			response.data.data.map((value: any) => {
				if (value.passed)
					return call(
						Course.download,
						`certificate/${value.userId}/${courseId}/download?institutionName=${institutionName}`,
						`${value.user} - ${value.courseName}.pdf`,
					);
			}),
		);
	} else {
		yield put(
			notificationActions.add(
				'info',
				'No hay información para descargar',
			),
		);
	}

	yield put(courseActions.set('isDownloadingBulk', false));
}

function* downloadUserCertificate({ payload }: any) {
	const { userId, courseId, userName, courseName, institutionName } = payload;
	yield call(
		Course.download,
		`certificate/${userId}/${courseId}/download?institutionName=${institutionName}`,
		`${userName} - ${courseName}.pdf`,
	);
}

function* remove({ payload }: any) {
	yield call(Course.delete, payload.id);
	yield put(notificationActions.add('success', 'Curso eliminado'));
	yield history.push('/app/courses');
}

export default function* index() {
	yield takeLatest(
		COURSE_TYPES.GET_ALL,
		sagaWrapper(getAll, courseActions.setAll([])),
	);
	yield takeLatest(
		COURSE_TYPES.GET_ONE,
		sagaWrapper(getOne, courseActions.setOne({})),
	);
	yield takeLatest(COURSE_TYPES.CREATE, sagaWrapper(create));
	yield takeLatest(COURSE_TYPES.UPDATE, sagaWrapper(update));
	yield takeLatest(COURSE_TYPES.GET_EXAM, sagaWrapper(getExam));
	yield takeLatest(
		COURSE_TYPES.GET_INSTITUTION_COURSES,
		sagaWrapper(getInstitutionCourses),
	);
	yield takeLatest(
		COURSE_TYPES.GET_USERS_COURSES,
		sagaWrapper(getUsersCourses),
	);
	yield takeLatest(
		COURSE_TYPES.SAVE_USERS_COURSES,
		sagaWrapper(saveUsersCourses),
	);
	yield takeLatest(COURSE_TYPES.GET_BY_USER, sagaWrapper(getByUser));
	yield takeLatest(
		COURSE_TYPES.GET_BY_INSTITUTION,
		sagaWrapper(getByInstitution),
	);
	yield takeLatest(COURSE_TYPES.COURSE_STARTED, sagaWrapper(courseStarted));
	yield takeLatest(COURSE_TYPES.COURSE_FINISHED, sagaWrapper(courseFinished));
	yield takeLatest(
		COURSE_TYPES.UPDATE_INSTRUCTIONS,
		sagaWrapper(updateInstructions),
	);
	yield takeLatest(COURSE_TYPES.GET_REPORT, sagaWrapper(getReport));
	yield takeLatest(
		COURSE_TYPES.DOWNLOAD_REPORT,
		sagaWrapper(
			downloadReport,
			courseActions.set('isDownloadingReport', false),
		),
	);
	yield takeLatest(
		COURSE_TYPES.GET_EXAM_REVIEW,
		sagaWrapper(getExamReview, courseActions.setExamReview([])),
	);
	yield takeLatest(
		COURSE_TYPES.SET_ANSWERED_SECTION,
		sagaWrapper(setAnsweredSection),
	);
	yield takeLatest(
		COURSE_TYPES.GET_USER_CERTIFICATE,
		sagaWrapper(getUserCertificate),
	);
	yield takeLatest(
		COURSE_TYPES.DOWNLOAD_USER_EXAM,
		sagaWrapper(
			downloadUserExam,
			courseActions.set('isDownloadingBulk', false),
		),
	);
	yield debounce(
		3000,
		COURSE_TYPES.DOWNLOAD_ALL_USERS_EXAM,
		sagaWrapper(
			downloadAllUsersExams,
			courseActions.set('isDownloadingBulk', false),
		),
	);
	yield debounce(
		3000,
		COURSE_TYPES.DOWNLOAD_USERS_CERTIFICATE,
		sagaWrapper(
			downloadUsersCertificates,
			courseActions.set('isDownloadingBulk', false),
		),
	);
	yield takeLatest(
		COURSE_TYPES.DOWNLOAD_USER_CERTIFICATE,
		sagaWrapper(downloadUserCertificate),
	);
	yield takeLatest(COURSE_TYPES.REMOVE, sagaWrapper(remove));
}
